/** Display state derived from a record's `crmSyncStatus` status string. */
export type SyncAltoStatus =
  | "synced"
  | "in_progress"
  | "pending"
  | "paused"
  | "checkpoint"
  | "failed"
  | "unknown";

/** The choices the list filters offer, keyed by the value sent to the API. */
export type SyncAltoFilterGroup = "synced" | "in_progress" | "pending" | "checkpoint" | "failed";

/**
 * Filter menu contents, in display order. `apiValue` is the `crmSyncStatus`
 * value the list endpoints filter on; NONE stands for rows with no stored
 * status yet, which the Sync column renders as "Sync pending".
 */
export const SYNC_ALTO_FILTER_GROUPS: { id: SyncAltoFilterGroup; label: string; apiValue: string }[] = [
  { id: "synced", label: "Synced", apiValue: "COMPLETED" },
  { id: "in_progress", label: "Syncing", apiValue: "IN_PROGRESS" },
  { id: "pending", label: "Pending", apiValue: "NONE" },
  { id: "checkpoint", label: "Checkpoint", apiValue: "CHECKPOINT" },
  { id: "failed", label: "Failed", apiValue: "FAILED" },
];

export const SYNC_ALTO_FILTER_GROUP_IDS: SyncAltoFilterGroup[] = SYNC_ALTO_FILTER_GROUPS.map(
  (group) => group.id,
);

/**
 * Comma-separated `crmSyncStatus` query value for the selected groups, or
 * undefined when everything is selected — no param means no narrowing.
 */
export function crmSyncStatusFilterParam(selected: SyncAltoFilterGroup[]): string | undefined {
  if (selected.length === SYNC_ALTO_FILTER_GROUPS.length) return undefined;
  return SYNC_ALTO_FILTER_GROUPS.filter((group) => selected.includes(group.id))
    .map((group) => group.apiValue)
    .join(",");
}

const STATUS_BY_VALUE: Record<string, SyncAltoStatus> = {
  COMPLETED: "synced",
  SUCCESS: "synced",
  IN_PROGRESS: "in_progress",
  RUNNING: "in_progress",
  PENDING: "pending",
  QUEUED: "pending",
  PAUSE: "paused",
  PAUSED: "paused",
  // Atlas-only record (e.g. an availability entry) — deliberately not pushed to the CRM.
  CHECKPOINT: "checkpoint",
  FAILED: "failed",
  ERROR: "failed",
};

/**
 * `crmSyncStatus` reaches the UI from list payloads and the `x-crm-sync-status` response
 * header, so coerce defensively rather than assuming a trimmed string.
 */
export function normalizeSyncAltoStatus(value: unknown): SyncAltoStatus {
  const key = String(value ?? "").trim().toUpperCase();
  return STATUS_BY_VALUE[key] ?? "unknown";
}

/** Statuses where a sync is already under way, so another can't be started. */
export function isSyncAltoSyncing(status: SyncAltoStatus): boolean {
  return status === "in_progress" || status === "pending";
}

/**
 * Display names for the CRMs Atlas integrates with, keyed by the uppercase
 * `sourceCrm` value records carry. Anything unknown falls back to a title-cased
 * version of the raw value, so a new CRM reads sensibly before it is listed here.
 */
const CRM_DISPLAY_NAMES: Record<string, string> = {
  ALTO: "Alto",
  PROPCO: "Propco",
  REAPIT: "Reapit",
  APEX27: "Apex27",
};

/**
 * Label for the CRM a record belongs to — e.g. "Alto", "Propco" — used in the
 * sync messages on the detail drawers. Falls back to "CRM" when the record
 * carries no `sourceCrm`, so the message stays readable either way.
 */
export function crmDisplayName(sourceCrm: unknown): string {
  const raw = String(sourceCrm ?? "").trim();
  if (!raw) return "CRM";
  return (
    CRM_DISPLAY_NAMES[raw.toUpperCase()] ??
    raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
  );
}
